import { CenteredFloatingActions } from "@/components/floating-actions/FloatingActions";
import { MdsButton, MdsButtonVariant } from "@/design-system/components/button";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import {
  AppStoreAudioCaptureStore,
  RecordingStatus,
} from "@/store/audio-capture/AppStoreAudioCaptureStore";
import { noop } from "lodash-es";
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { MdsIconButton } from "@/design-system/components/icon-button";
import { AnimatePresence, motion } from "motion/react";
import { AudioCaptureWaveForm } from "@/components/layout/components/AudioCaptureWaveForm";
import { mdsColors } from "@/design-system/foundations/colors";
import { DeleteRecordingModal } from "@/components/modal/delete-recording/DeleteRecordingModal";
import { MdsTooltip } from "@/design-system/components/tooltip/MdsTooltip";
import { MdsTooltipPlacement } from "@/design-system/components/tooltip";

interface AudioCaptureActionsProps {
  noteId: string;
  verticalOffset?: number;
}

export const AudioCaptureActions = observer(function AudioCaptureActions({
  noteId,
  verticalOffset,
}: AudioCaptureActionsProps) {
  const { store } = useAppStore();
  const [isExpanded, setIsExpanded] = useState(false);

  const maxHistoryBars = 80;
  const [historyBarHeights, setHistoryBarHeights] = useState<number[]>(
    Array.from({ length: maxHistoryBars }, () => 0.0)
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const handleDelete = () => {
    store.audioCapture.clearRecording();
    setShowDeleteModal(false);
  };

  useEffect(() => {
    const analyser = store.audioCapture.analyser;
    if (!analyser) return;

    const timeDomainData = new Uint8Array(analyser.fftSize);

    let frameCounter = 0;

    let animationFrameId: number;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      // Only update every 12th frame
      frameCounter++;
      if (frameCounter % 12 !== 0) return;

      const isRecording = store.audioCapture.status === RecordingStatus.RECORDING;

      analyser.getByteTimeDomainData(timeDomainData);

      if (isRecording) {
        // Calculate RMS for history bars
        let rms = 0;
        for (let i = 0; i < timeDomainData.length; i++) {
          const sample = (timeDomainData[i] - 128) / 128.0;
          rms += sample * sample;
        }
        rms = Math.sqrt(rms / timeDomainData.length);

        // Calculate bar height with logarithmic scaling for better visualization of lower volumes
        let barHeight = Math.log10(rms * 65 + 1) / Math.log10(66); // This maps 0-1 to 0-1 with logarithmic scaling, boosting lower values by 50% more
        barHeight = Math.max(barHeight, 0.1);
        barHeight = Math.min(barHeight, 1);

        setHistoryBarHeights(prev => {
          const newHeights = [...prev, barHeight];
          if (newHeights.length > maxHistoryBars) {
            return newHeights.slice(-maxHistoryBars);
          }
          return newHeights;
        });
      }
    };
    animate();
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [store.audioCapture.analyser, store.audioCapture.status]);

  const durationText = store.audioCapture.formattedDuration;
  const recordingStatus = store.audioCapture.status;
  const currentMicrophoneLabel = store.audioCapture.activeAudioInputDeviceLabel;
  const transcript = store.audioCapture.transcript;
  const hasTranscript = transcript.length > 0;
  const transcriptPaused = store.audioCapture.isTranscriptionPaused;
  const isRecordingWithoutTranscription = store.audioCapture.isRecordingWithoutTranscription;

  const isVoiceCaptureEnabled = store.spaceAccountFeatureFlags.getFlagStatus("audio-capture");
  const shouldDisplayRetryButton = store.audioCapture.error && canPressDone(store.audioCapture);

  // We use this to delay the rendering of the inner components until the animation is complete
  const [showInnerComponents, setShowInnerComponents] = useState(false);

  // We use this to scroll to the bottom to the transcript as it is appended to
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // Check all conditions that might make the transcript container not exist yet.
    if (isExpanded && hasTranscript && showInnerComponents && transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [hasTranscript, isExpanded, showInnerComponents, transcript]);

  useEffect(() => {
    if (!isExpanded) setShowInnerComponents(false);
  }, [isExpanded]);

  // Auto-expand when we get a transcript for the first time
  // If the user manually closes the transcript, we don't want to auto-expand again
  const [userManuallyClosed, setUserManuallyClosed] = useState(false);
  useEffect(() => {
    if ((hasTranscript || isRecordingWithoutTranscription) && !isExpanded && !userManuallyClosed) {
      setIsExpanded(true);
    }
  }, [hasTranscript, isExpanded, userManuallyClosed, isRecordingWithoutTranscription]);

  if (!isVoiceCaptureEnabled) return null;
  return (
    <>
      <DeleteRecordingModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
      />
      <CenteredFloatingActions isOpen={true} verticalOffset={verticalOffset}>
        <StyledContainer
          isExpanded={isExpanded && hasTranscript}
          initial={{ height: 60, width: 300 }}
          animate={{ height: isExpanded ? "auto" : 60, width: isExpanded ? 600 : "auto" }}
          transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
          onAnimationComplete={() => {
            if (isExpanded) setShowInnerComponents(true);
          }}
        >
          {showInnerComponents && isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <StyledHeader>
                <StyledHeaderTop>
                  <MdsTooltip
                    config={{
                      placement: MdsTooltipPlacement.Top,
                      label:
                        recordingStatus !== RecordingStatus.RECORDING
                          ? "Not recording"
                          : currentMicrophoneLabel
                            ? `Recording from device "${currentMicrophoneLabel}"`
                            : "No microphone found",
                    }}
                  >
                    <StyledHeaderRow>
                      <AudioCaptureWaveForm barsColor={mdsColors().grey.x500} />
                      <StyledHeaderTitle>Voice Recording</StyledHeaderTitle>
                    </StyledHeaderRow>
                  </MdsTooltip>
                  <StyledHeaderRow>
                    <MdsIconButton
                      iconKind={MdsIconKind.AngleDown}
                      onClick={() => {
                        setIsExpanded(false);
                        setUserManuallyClosed(true);
                      }}
                    />
                    <MdsIconButton
                      iconKind={MdsIconKind.Exit}
                      onClick={() => setShowDeleteModal(true)}
                    />
                  </StyledHeaderRow>
                </StyledHeaderTop>
                <StyledHeaderBottom>
                  <HistoryBarsContainer>
                    {historyBarHeights.map((height, index) => (
                      <HistoryBar
                        key={index}
                        style={{
                          height: `${height * 100}%`,
                          transform: `translateX(${(index / maxHistoryBars) * 100}%) translateY(-50%)`,
                          left: `${(index / maxHistoryBars) * 100}%`,
                        }}
                      />
                    ))}
                  </HistoryBarsContainer>
                  <StyledDurationText>{durationText}</StyledDurationText>
                </StyledHeaderBottom>
              </StyledHeader>
              {hasTranscript && (
                <StyledTranscriptContainer
                  ref={transcriptContainerRef}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
                >
                  <StyledTranscriptText>
                    <span>{transcript}</span>
                    {transcriptPaused && (
                      <TranscriptStatus>
                        <TranscriptStatusIcon kind={MdsIconKind.CloudSlash} />
                        <span>Transcription paused, will resume automatically</span>
                      </TranscriptStatus>
                    )}
                    <MicrophoneDisconnectedNotice />
                  </StyledTranscriptText>
                </StyledTranscriptContainer>
              )}
              {!hasTranscript &&
                (isRecordingWithoutTranscription ||
                  recordingStatus === RecordingStatus.RECORDING) && (
                  <StyledTranscriptContainer>
                    {isRecordingWithoutTranscription && (
                      <TranscriptStatus>
                        <TranscriptNotSupportedStatusIcon kind={MdsIconKind.TextSlash} />
                        <TranscriptionNotSupportedNotice>
                          Transcription not supported on this application
                        </TranscriptionNotSupportedNotice>
                      </TranscriptStatus>
                    )}
                    {recordingStatus === RecordingStatus.RECORDING && (
                      <TranscriptStatus>
                        <TranscriptStatusIcon kind={MdsIconKind.Microphone} />
                        <span>Listening...</span>
                      </TranscriptStatus>
                    )}
                    <MicrophoneDisconnectedNotice />
                  </StyledTranscriptContainer>
                )}
            </motion.div>
          )}
          <StyledButtonsContainer isExpanded={isExpanded}>
            {!isExpanded && (
              <>
                <AudioCaptureWaveForm barsColor={mdsColors().grey.x500} />
                <StyledDurationText>{durationText}</StyledDurationText>
                <MdsIconButton iconKind={MdsIconKind.AngleUp} onClick={() => setIsExpanded(true)} />
              </>
            )}
            {recordingStatus === RecordingStatus.RECORDING && (
              <MdsButton
                label="Pause"
                variant={MdsButtonVariant.Outlined}
                onClick={() => store.audioCapture.pauseRecording()}
                isDisabled={store.audioCapture.isMicrophoneDisconnected}
              />
            )}
            {recordingStatus === RecordingStatus.PAUSED && (
              <MdsButton
                label="Resume"
                variant={MdsButtonVariant.Outlined}
                onClick={() => store.audioCapture.resumeRecording()}
                isDisabled={store.audioCapture.isMicrophoneDisconnected}
              />
            )}
            {recordingStatus === RecordingStatus.LOCKED && (
              <MdsButton
                // Locked state means we haven't initialized or started recording yet,
                // but we display resume to the user.
                label="Resume"
                variant={MdsButtonVariant.Outlined}
                onClick={async () => {
                  await store.audioCapture.initializeAndStartRecording(noteId);
                }}
                isDisabled={
                  isFinishedRecording(recordingStatus) ||
                  store.audioCapture.isMicrophoneDisconnected
                }
              />
            )}
            {(recordingStatus === RecordingStatus.IDLE ||
              recordingStatus === RecordingStatus.INITIALIZING) && (
              <MdsButton
                label="Start"
                variant={MdsButtonVariant.Outlined}
                onClick={async () => {
                  await store.audioCapture.initializeAndStartRecording(noteId);
                }}
                isDisabled={isFinishedRecording(recordingStatus)}
                isLoading={recordingStatus === RecordingStatus.INITIALIZING}
              />
            )}
            {isProcessing(recordingStatus) && (
              <MdsButton
                label="Processing..."
                variant={MdsButtonVariant.Outlined}
                onClick={noop}
                isLoading={true}
              />
            )}
            {isFinishedRecording(recordingStatus) && !isProcessing(recordingStatus) && (
              <MdsButton
                label="Cancel"
                variant={MdsButtonVariant.Outlined}
                onClick={() => store.audioCapture.clearRecording()}
              />
            )}
            <MdsButton
              label={shouldDisplayRetryButton ? "Retry" : "Done"}
              variant={MdsButtonVariant.FilledDark}
              onClick={async () => {
                if (
                  recordingStatus === RecordingStatus.RECORDING ||
                  recordingStatus === RecordingStatus.PAUSED
                )
                  await store.audioCapture.stopRecording();
                await store.audioCapture.processRecording();
              }}
              isDisabled={!canPressDone(store.audioCapture)}
            />
          </StyledButtonsContainer>
        </StyledContainer>
      </CenteredFloatingActions>
    </>
  );
});

const canPressDone = (store: AppStoreAudioCaptureStore): boolean => {
  if (!store.releaseRecordingLock || store.isMicrophoneDisconnected) return false;

  const status = store.status;
  if (status === RecordingStatus.LOCKED) return true;
  if (status === RecordingStatus.PAUSED) return true;
  if (status === RecordingStatus.RECORDING) return true;
  if (status === RecordingStatus.STOPPED) return true;
  if (status === RecordingStatus.UPLOADED) return true;
  return false;
};

const isFinishedRecording = (status: RecordingStatus): boolean => {
  if (status === RecordingStatus.STOPPED) return true;
  if (status === RecordingStatus.UPLOADING) return true;
  if (status === RecordingStatus.UPLOADED) return true;
  if (status === RecordingStatus.PROCESSING) return true;
  return false;
};

const isProcessing = (status: RecordingStatus): boolean => {
  if (status === RecordingStatus.PROCESSING) return true;
  if (status === RecordingStatus.UPLOADING) return true;
  return false;
};

const StyledContainer = styled(motion.div)<{ isExpanded?: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey.x25};
  border-radius: ${({ theme }) => theme.borderRadius.mediumLarge}px;
  box-shadow: ${({ theme }) => theme.shadows.hoverCard};
  font-size: ${({ theme }) => theme.fontSizes.small};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  max-width: 600px;
  width: 100%;
  min-width: 300px;

  @media (max-width: 768px) {
    min-width: 280px;
    max-width: 95vw;
  }
`;

const StyledButtonsContainer = styled.div<{ isExpanded?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: ${({ isExpanded }) => (isExpanded ? "flex-end" : "space-between")};
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
`;

const StyledDurationText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.grey.x600};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  width: 50px;
  text-align: right;
`;

const StyledTranscriptContainer = styled(motion.div)`
  padding: ${({ theme }) => theme.spacing.md};
  overflow-y: auto;
  max-height: 100px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StyledTranscriptText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.grey.x700};
  line-height: ${({ theme }) => theme.lineHeights.smedium};
  white-space: pre-wrap;
`;

const TranscriptStatus = styled.div`
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.grey.x500};
  display: flex;
  flex-direction: row;
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  gap: ${({ theme }) => theme.spacing.xs};
  line-height: ${({ theme }) => theme.lineHeights.xsmall};
  width: 100%;
`;

const TranscriptStatusIcon = styled(MdsIcon)`
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  height: 16px;
  width: 16px;

  & svg {
    color: ${({ theme }) => theme.colors.grey.x500};
  }
`;

const TranscriptNotSupportedStatusIcon = styled(MdsIcon)`
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  height: 16px;
  width: 16px;

  & svg {
    color: ${({ theme }) => theme.colors.red.x500};
  }
`;

const TranscriptionNotSupportedNotice = styled.span`
  color: ${({ theme }) => theme.colors.red.x500};
`;

const StyledHeader = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.grey.x25};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
`;

const StyledHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StyledHeaderTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.grey.x900};
`;

const StyledHeaderTop = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const StyledHeaderBottom = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  min-width: 0;
`;

const HistoryBarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0 4px;
  position: relative;
  overflow: hidden;
`;

const HistoryBar = styled.div`
  border-radius: 2px;
  width: 0.7%;
  margin: 0 0.35%;
  background-color: #ccc;
  transition: transform 0.2s ease-in-out;
  position: absolute;
  top: 50%;
  transform: translateX(0) translateY(-50%);
  will-change: transform;
`;

const MicrophoneDisconnectedNotice = observer(function MicrophoneDisconnectedNotice() {
  const { store } = useAppStore();

  return (
    <AnimatePresence>
      {store.audioCapture.isMicrophoneDisconnected && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{
            height: 0,
            opacity: 0,
            transition: { delay: 0.8, duration: 0.2, ease: "easeInOut" },
          }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <TranscriptStatus>
            <TranscriptStatusIcon kind={MdsIconKind.MicrophoneSlash} />
            <span>Microphone was disconnected, restarting voice mode...</span>
          </TranscriptStatus>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
